import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import { categories } from '@/mocks/categories';
import { getMangaByCategory, allManga } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'school-life');
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const categoryManga = activeCategory === 'all' ? allManga : getMangaByCategory(currentCategory.id);

  const sortedManga = [...categoryManga].sort((a, b) => {
    if (sortBy === 'hot') return b.views - a.views;
    if (sortBy === 'new') return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    return b.rating - a.rating;
  });

  const featuredManga = sortedManga.slice(0, 3);
  const editorPick = sortedManga[0];

  const pageTitle = `${currentCategory.name}漫画 - 免费在线阅读 - 178漫画网`;
  const pageDesc = `178漫画网${currentCategory.name}分类，${currentCategory.description}共收录${currentCategory.mangaCount}部精选漫画，免费下拉式阅读，无需注册付费。`;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category/school-life' },
    { label: currentCategory.name },
  ];

  const listSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `http://91cghlw.cn/category/${activeCategory}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '178漫画网',
      url: 'http://91cghlw.cn/',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(listSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <Breadcrumb items={breadcrumbItems} />

          {/* Category Hero */}
          <section className="relative w-full overflow-hidden bg-background-100">
            <div className="w-full px-4 md:px-6 py-12 md:py-20">
              <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                <div className="w-full lg:w-2/5 text-center lg:text-left">
                  <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading text-foreground-900 leading-tight">
                    {currentCategory.name}
                  </h1>
                  <p className="mt-4 text-sm md:text-base text-foreground-500 leading-relaxed">
                    {currentCategory.description}
                  </p>
                  <p className="mt-3 text-xs text-foreground-400">
                    共收录 <strong>{currentCategory.mangaCount}</strong> 部漫画作品
                  </p>
                </div>
                <div className="w-full lg:w-3/5">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 sm:col-span-1">
                      <div className="relative rounded-2xl overflow-hidden aspect-[3/4]">
                        <img
                          src="https://readdy.ai/api/search-image?query=Japanese%20manga%20style%20illustration%20of%20school%20life%20scene%2C%20students%20in%20classroom%2C%20youthful%20energy%2C%20bright%20sunlight%2C%20clean%20anime%20art%2C%20warm%20atmosphere%2C%20summer%20vibe&width=600&height=800&seq=cat-hero-01&orientation=portrait"
                          alt={currentCategory.name}
                          title={`${currentCategory.name}漫画 - 178漫画网`}
                          className="w-full h-full object-cover object-top"
                        />
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                          <span className="text-white text-sm font-medium bg-primary-500/90 px-3 py-1 rounded-full whitespace-nowrap">
                            {currentCategory.name}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2 sm:col-span-1 flex flex-col gap-4">
                      <div className="grid grid-cols-2 gap-4">
                        {featuredManga.slice(0, 4).map((manga) => (
                          <Link
                            key={manga.id}
                            to={`/article/${manga.id}`}
                            className="relative rounded-xl overflow-hidden aspect-[4/5] group cursor-pointer"
                          >
                            <img
                              src={manga.cover}
                              alt={manga.title}
                              title={`${manga.title} - 178漫画网`}
                              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                              <span className="text-white text-xs block truncate">{manga.title}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="sticky top-16 z-30 bg-background-50 border-b border-background-200">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                        activeCategory === cat.slug
                          ? 'bg-primary-500 text-white'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Sort & Results */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                  <div>
                    <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                      {currentCategory.name}
                      <span className="text-sm font-normal text-foreground-500 ml-2">
                        共 {sortedManga.length} 部作品
                      </span>
                    </h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                    <div className="flex bg-background-100 rounded-full p-0.5">
                      {[
                        { key: 'hot', label: '最热' },
                        { key: 'new', label: '最新' },
                        { key: 'rating', label: '评分' },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          onClick={() => setSortBy(opt.key as typeof sortBy)}
                          className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                            sortBy === opt.key
                              ? 'bg-white text-foreground-900 shadow-sm'
                              : 'text-foreground-500 hover:text-foreground-700'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Editor Pick */}
                {editorPick && (
                  <div className="mb-8">
                    <div className="bg-foreground-900 rounded-2xl overflow-hidden">
                      <div className="flex flex-col lg:flex-row">
                        <div className="lg:w-1/3 p-8 flex flex-col justify-center">
                          <span className="px-3 py-1 bg-accent-500 text-white text-xs rounded-full whitespace-nowrap self-start">
                            编辑精选
                          </span>
                          <h3 className="text-2xl font-bold text-white mt-4">{editorPick.title}</h3>
                          <p className="text-sm text-white/70 mt-2 line-clamp-3">{editorPick.description}</p>
                          <div className="flex items-center gap-4 mt-4">
                            <span className="text-xs text-white/50">{editorPick.author}</span>
                            <span className="text-xs text-white/50">{editorPick.latestChapter}</span>
                          </div>
                          <Link
                            to={`/article/${editorPick.id}`}
                            className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 bg-white text-foreground-900 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-white/90 transition-colors cursor-pointer self-start"
                          >
                            开始阅读
                            <i className="ri-arrow-right-line"></i>
                          </Link>
                        </div>
                        <div className="lg:w-2/3">
                          <div className="relative h-64 lg:h-80">
                            <img
                              src={editorPick.cover}
                              alt={editorPick.title}
                              title={`${editorPick.title} - 178漫画网`}
                              className="w-full h-full object-cover object-top"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-foreground-900 via-foreground-900/50 to-transparent lg:bg-gradient-to-l"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Manga Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {sortedManga.map((manga) => (
                    <MangaCard key={manga.id} manga={manga} variant="default" />
                  ))}
                </div>

                {sortedManga.length === 0 && (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                      <i className="ri-book-2-line text-2xl"></i>
                    </div>
                    <p className="mt-4 text-foreground-500">该分类暂无漫画作品</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}